import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector, useDispatch } from "react-redux";
import CoachingFeedFormDialog from "./coachingFeedDialog";
import {
  deleteCoachingFeed,
  fetchCoachingFeed,
} from "reduxStore/actions/coachingFeedActions";
import { useInView } from "react-intersection-observer";
import { useParams } from "react-router-dom";
import FlexBetween from "components/FlexBetween";

const CoachingFeedTable = () => {
  const dispatch = useDispatch();
  const isLargeScreen = useMediaQuery("(min-width:1000px)");

  const { palette } = useTheme();
  const primary = palette.primary.main;
  const primaryLight = palette.primary.light;
  const background = palette.background.default;
  const backgroundAlt = palette.background.alt;

  const viewingUserId = useParams().userId;
  const user = useSelector((state) => state.user);
  const userId = viewingUserId || user?._id;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const [viewMode, setViewMode] = useState("date");
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const { ref, inView } = useInView();
  const limit = 10;

  useEffect(() => {
    const loadEntries = async () => {
      const res = await dispatch(fetchCoachingFeed({ userId, page, limit }));
      if (res?.data?.length > 0) {
        setEntries((prev) => [...prev, ...res.data]);
        setPage((prev) => prev + 1);
        setHasMore(res.data.length === limit);
      } else {
        setHasMore(false);
      }
    };

    if (inView && hasMore) {
      loadEntries();
    }
  }, [
    inView,
    userId,
    refetchTrigger,
    page,
    limit,
    dispatch,
    viewingUserId,
    hasMore,
  ]); // ✅ include refetchTrigger

  useEffect(() => {
    setEntries([]);
    setPage(0);
    setHasMore(true);
  }, [viewingUserId]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEntry(null);
    setEntries([]);
    setPage(0);
    setHasMore(true);
    setRefetchTrigger((prev) => prev + 1); // ✅ trigger re-fetch
  };

  let sortedEntries = [...entries];
  if (viewMode === "date") {
    sortedEntries.sort((a, b) => new Date(a.date) - new Date(b.date));
  } else {
    sortedEntries.sort((a, b) => {
      if (a.phase === b.phase) {
        return new Date(a.date) - new Date(b.date);
      }
      return a.phase.localeCompare(b.phase);
    });
  }

  function handlePhaseNames(orginalName) {
    if (orginalName === "Review") return "Rehearse (and review)";
    else if (orginalName === "Model") return "Model (and script)";
    else return orginalName;
  }

  return (
    <Box mt="2rem">
      <FlexBetween my={2}>
        <Button
          variant="contained"
          onClick={() => {
            setSelectedEntry(null); // ensure new entry
            setDialogOpen(true);
          }}
        >
          Add Coaching Entry
        </Button>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newView) => {
            if (newView !== null) setViewMode(newView);
          }}
          size="small"
        >
          <ToggleButton value="date">Sort by Date</ToggleButton>
          <ToggleButton value="phase">Group by Phase</ToggleButton>
        </ToggleButtonGroup>
      </FlexBetween>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: primary }}>
            <TableCell sx={{ color: backgroundAlt, fontWeight: "bold" }}>
              Date / Phase
            </TableCell>
            <TableCell sx={{ color: backgroundAlt, fontWeight: "bold" }}>
              Focus / Action Step
            </TableCell>
            <TableCell sx={{ color: backgroundAlt, fontWeight: "bold" }}>
              Coaching Feed
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEntries.map((entry, index) => {
            const showPhaseHeader =
              viewMode === "phase" &&
              (index === 0 || sortedEntries[index - 1].phase !== entry.phase);

            return (
              <React.Fragment key={entry._id}>
                {showPhaseHeader && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{ backgroundColor: primaryLight }}
                    >
                      <Typography fontWeight="bold">{entry.phase}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ backgroundColor: background }}>
                  <TableCell
                    sx={{
                      verticalAlign: "top",
                      width: isLargeScreen ? "15%" : "100%",
                    }}
                  >
                    <Typography fontWeight="bold">
                      {new Date(entry.date).toLocaleDateString("en-GB")}
                    </Typography>
                    <Typography>{handlePhaseNames(entry.phase)}</Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      verticalAlign: "top",
                      width: isLargeScreen ? "25%" : "100%",
                    }}
                  >
                    {entry.focusUrl ? (
                      <Link
                        href={entry.focusUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {entry.focusUrl
                          .replace(/^https?:\/\//, "")
                          .slice(0, 50)}
                        ...
                      </Link>
                    ) : (
                      <Typography>-</Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: "pre-line", verticalAlign: "top" }}
                  >
                    {entry.notes}
                    <Box display="flex" justifyContent="flex-end" mt={1}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedEntry(entry);
                          setDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEntryToDelete(entry);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}

          {hasMore && (
            <TableRow ref={ref}>
              <TableCell colSpan={3} align="center">
                <Typography variant="body2">Loading more...</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {entries.length === 0 && !hasMore && (
        <Typography mt={3} textAlign="center" color="text.secondary">
          No coaching feed entries available.
        </Typography>
      )}

      <CoachingFeedFormDialog
        open={dialogOpen}
        handleClose={handleCloseDialog}
        initialData={selectedEntry}
        userId={selectedEntry?.user || userId}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Are you sure you want to delete this entry?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (entryToDelete) {
                await dispatch(
                  deleteCoachingFeed(entryToDelete._id, entryToDelete.user)
                );
              }
              setDeleteDialogOpen(false);
              setEntryToDelete(null);
              setEntries([]);
              setPage(0);
              setHasMore(true);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoachingFeedTable;
